import { Button, Heading2 } from '@casavo/base-ui'
import { Formik, Form } from 'formik'
import { Translate } from 'next-translate'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'
import * as Yup from 'yup'

import Disclaimer from '@/components/Disclaimer'
import FormikInput from '@/components/FormFields/FormikInput'
import { ModalBase } from '@/components/Modal/ModalBase'
import { PriorityBadge } from '@/components/PriorityCardBadge'
import { CasavoVisitInterest } from '@/containers/PriorityDetail/useExpressPotentialPropertyVisitInterest'
import { useAuthentication } from '@/context/AuthProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { deferredRequest } from '@/utils/deferredRequest'
import { getPhoneNumberValidator } from '@/utils/phoneNumberValidator'
import { getPrefixFromCountryCode, langToCountryCode } from '@/utils/prefix'
import { CasavoBuyerInfo } from '@/utils/priority-detail-utils'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'

import { visitRequestModalStyle } from './styles.css'

type FormFields = Yup.InferType<ReturnType<typeof getValidationSchema>>

type Props = {
  buyerInfo?: CasavoBuyerInfo
  expressVisitInterest: (args: CasavoVisitInterest) => Promise<void>
  setShowToastError: Dispatch<SetStateAction<boolean>>
  thankYouPageUrl: string
}
const getValidationSchema = (t: Translate, hasPhone: boolean) =>
  Yup.object().shape({
    name: Yup.string().required(t('common:required-field-message')),
    phone: getPhoneNumberValidator(t('common:required-field-message'), t('common:invalid-phone-message'), hasPhone),
    phonePrefix: Yup.string().required(),
  })

const getInitialValues = (lang: Locale, name: string, phone?: string) => {
  return {
    name,
    phone: phone || '',
    phonePrefix: getPrefixFromCountryCode(langToCountryCode(lang)),
  }
}

export const VisitRequestModal: React.FC<Props> = ({
  buyerInfo,
  expressVisitInterest,
  setShowToastError,
  thankYouPageUrl: thankyouPageUrl,
}) => {
  const { lang, t } = useTranslation()
  const router = useRouter()
  const validationSchema = useMemo(() => getValidationSchema(t, Boolean(buyerInfo?.phone)), [])
  const submitLabel = t('priority:Priority_DetailPage_FloatingBanner_CTA')
  const { isLoading, user } = useAuthentication()
  const name = user?.first_name && user?.last_name && !isLoading ? `${user?.first_name} ${user?.last_name}` : ''
  const initialValues = useMemo(() => getInitialValues(lang, name, buyerInfo?.phone), [name])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const showFormikInputWithPhonePrefix = !buyerInfo?.phone

  const sendVisitInterest = async ({ phonePrefix, ...values }: FormFields) => {
    const phone = showFormikInputWithPhonePrefix ? `${phonePrefix}${values.phone}` : values.phone
    const isLogged = buyerInfo?.email === undefined
    if (isLogged) {
      return expressVisitInterest({ buyerPhoneNumber: phone, isLogged, name: values.name })
    } else {
      return expressVisitInterest({
        buyerPhoneNumber: phone,
        crmContactId: buyerInfo.crm_contact_id,
        email: buyerInfo.email,
        isLogged: false,
        name: values.name,
        score: buyerInfo.score,
      })
    }
  }

  const handleSubmit = async (values: FormFields) => {
    handleClick(GTMEventCategory.PriorityVisitModalForm, 'SubmitScheduleVisit')
    setIsSubmitting(true)

    try {
      await deferredRequest(sendVisitInterest(values), 1000)
      handleShow(GTMEventCategory.PriorityDetail, 'RequestVisit_Success')
      router.push(thankyouPageUrl)
    } catch {
      setShowToastError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalBase noPadding className={visitRequestModalStyle}>
      <div>
        <Heading2 className={sprinkles({ paddingX: '2xl', paddingY: '3xl' })}>
          {t('priority:Priority_PhoneNumberModal_Title')}
        </Heading2>
        <PriorityBadge />
        <Formik
          initialValues={initialValues}
          validateOnMount={true}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isValid }) => (
            <Form>
              <div className={sprinkles({ paddingX: '2xl' })}>
                <div className={sprinkles({ marginBottom: 's' })}>
                  <FormikInput required label={t('contact-form:name')} name="name" type="text" />
                </div>
                <div className={sprinkles({ marginBottom: '3xl' })}>
                  {showFormikInputWithPhonePrefix ? (
                    <FormikInput
                      required
                      inputMode="tel"
                      label={t('contact-form:phone')}
                      name="phone"
                      prefixName="phonePrefix"
                      type="tel"
                    />
                  ) : (
                    <FormikInput required inputMode="tel" label={t('contact-form:phone')} name="phone" type="tel" />
                  )}
                </div>
              </div>

              <Disclaimer className={sprinkles({ marginBottom: 'l', paddingX: '3xl' })} cta={submitLabel} />
              <div className={sprinkles({ marginBottom: 'xl' })}>
                <Button
                  center
                  disabled={!isValid}
                  label={submitLabel}
                  loading={isSubmitting}
                  minWidth={220}
                  type="submit"
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </ModalBase>
  )
}
